// src/features/equipment/pages/EquipmentDetailsPage/components/EquipmentTasksCard.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Assignment as AssignmentIcon,
  Repeat,
  Loop as LoopIcon,
  PlaylistAdd as PlaylistAddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEquipment } from '../../../hooks/useEquipment';
import { useEquipmentTemplates } from '../../../hooks/useEquipmentTemplates';
import { getStateConfig } from '../../../../tasks/utils/taskStateUtils';
import { useTaskPermissions } from '../../../../auth/hooks/usePermissions';
import { RELATION_TYPES as RELATION_TYPES_CONFIG } from '../../../utils/enums';

// File utilities
import { formatFileSize } from '../../../../files/utils/formatFileSize';

// Components
import FilePreviewTooltip from '../../../../files/components/FilePreveiwTooltip';
import FileThumbnail from '../../../../files/components/FileThumbnail';
import StaffMember from '../../../../../components/common/StaffMember';


const RELATION_TYPES = RELATION_TYPES_CONFIG.map(type => type.value);
const PAGE_SIZE_OPTIONS = [10, 25, 50];

function renderAttachments(attachments = []) {
  if (attachments.length === 0) return <Typography variant="body2" color="text.secondary">-</Typography>;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {attachments.slice(0, 3).map((attachment) => {
        return (
          <FilePreviewTooltip key={attachment.attachment_id} file={attachment}>
            <span>
              <FileThumbnail fileId={attachment.file_id} filetype={attachment.filetype} filename={attachment.file_name} />
            </span>
          </FilePreviewTooltip>
        );
      })}
      {attachments.length > 3 && <Chip label={`+${attachments.length - 3}`} size="small" sx={{ height: 24 }} />}
    </Stack>
  );
}

export default function EquipmentTasksCard({ equipmentId, equipment }) {
  const { t } = useTranslation(['equipment', 'tasks', 'common']);
  const { canRead: canReadTasks } = useTaskPermissions();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(null);
  // Tasks per relation type, for the sidebar; the grid loads one page of the active type
  const [counts, setCounts] = useState({});
  const [countsLoading, setCountsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const { fetchEquipmentTasks, error } = useEquipment();
  const { templates: equipmentTemplates, fetchTemplates } = useEquipmentTemplates();

  // `${type}|${page}|${pageSize}` of the rows on screen, so a page already loaded isn't fetched twice
  const loadedPage = useRef(null);
  const pageKey = (type, model) => `${type}|${model.page}|${model.pageSize}`;

  const fetchPage = useCallback((type, model) => fetchEquipmentTasks(equipmentId, {
    relation_type: type, limit: model.pageSize, offset: model.page * model.pageSize,
  }), [equipmentId, fetchEquipmentTasks]);

  const showPage = useCallback((result, type, model) => {
    if (!result.success) return;
    setRows(result.data.data?.relationships || []);
    setRowCount(result.data.data?.total_count || 0);
    loadedPage.current = pageKey(type, model);
  }, []);

  // Counts first (limit 0), then straight away the first page of the first type that has
  // tasks, so the grid renders once with its rows instead of mounting empty and then fetching
  useEffect(() => {
    if (!canReadTasks) return undefined;
    let cancelled = false;
    setCountsLoading(true);
    (async () => {
      const result = await fetchEquipmentTasks(equipmentId, { limit: 0 });
      const byType = result.success ? (result.data.data?.counts_by_relation_type || {}) : {};
      const firstType = RELATION_TYPES.find(type => byType[type] > 0) ?? null;
      const model = { page: 0, pageSize: paginationModel.pageSize };
      const firstPage = firstType ? await fetchPage(firstType, model) : null;
      if (cancelled) return;
      if (firstPage) showPage(firstPage, firstType, model);
      setCounts(byType);
      setActiveTab(firstType);
      setPaginationModel(model);
      setCountsLoading(false);
    })();
    return () => { cancelled = true; };
    // Page size is read once here; later changes go through the page effect
  }, [equipmentId, fetchEquipmentTasks, fetchPage, showPage, canReadTasks]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canReadTasks || !activeTab || loadedPage.current === pageKey(activeTab, paginationModel)) return undefined;
    let cancelled = false;
    setPageLoading(true);
    fetchPage(activeTab, paginationModel).then((result) => {
      if (cancelled) return;
      showPage(result, activeTab, paginationModel);
      setPageLoading(false);
    });
    return () => { cancelled = true; };
  }, [fetchPage, showPage, canReadTasks, activeTab, paginationModel]);

  useEffect(() => {
    if (canReadTasks) fetchTemplates();
  }, [fetchTemplates, canReadTasks]);

  const availableTemplates = useMemo(() => {
    if (!equipment?.model || equipmentTemplates.length === 0) return [];
    const model = equipment.model.replaceAll(' ', '').toLowerCase();
    const match =
      equipmentTemplates.find(t => t.model?.replaceAll(' ', '').toLowerCase() === model) ??
      equipmentTemplates.find(t => model.startsWith(t.model?.replaceAll(' ', '').toLowerCase() ?? ''));
    return match?.task_templates ?? [];
  }, [equipmentTemplates, equipment]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    setRows([]);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);

  const getTaskCount = useCallback((type) => counts[type] || 0, [counts]);

  const handleViewTask = useCallback((taskId) => {
    navigate(`/tasks/${taskId}`);
  }, [navigate]);

  const handleOpenMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleUseTemplate = useCallback((template) => {
    navigate('/tasks/create', { 
      state: { 
        templateTask: template,
        equipmentId: equipmentId
      } 
    });
    handleCloseMenu();
  }, [navigate, equipmentId, handleCloseMenu]);

  const getFrequencyLabel = useCallback((template) => {
    if (!template.is_recurring) return t('tasks:catalog.frequency.one_time');
    
    const { frequency_interval, unit } = template;
    
    if (frequency_interval === 1) {
      return t(`tasks:catalog.frequency.every_singular.${unit}`);
    }
    
    return t(`tasks:catalog.frequency.every_plural`, { 
      interval: frequency_interval, 
      unit: t(`tasks:catalog.frequency.units.${unit}`) 
    });
  }, [t]);

  // Render template details for tooltip
  const renderTemplateDetails = useCallback((template) => (
    <Box sx={{ p: 1, maxWidth: 350 }}>
      <Box display="flex" alignItems="start" gap={1} mb={2}>
        {template.is_recurring ? (
          <LoopIcon fontSize="small" />
        ) : (
          <AssignmentIcon fontSize="small" />
        )}
        <Box flex={1}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {template.name}
          </Typography>
          <Chip
            label={getFrequencyLabel(template)}
            size="small"
            color={template.is_recurring ? 'secondary' : 'default'}
            sx={{ mb: 1 }}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 1.5, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

      <Typography variant="caption" display="block" mb={0.5} sx={{ opacity: 0.7 }}>
        {t('common:labels.description')}
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          whiteSpace: 'pre-line',
          mb: 2,
          fontSize: '0.875rem'
        }}
      >
        {template.description}
      </Typography>

      {template.is_recurring && (
        <>
          <Divider sx={{ my: 1.5, borderColor: 'rgba(255, 255, 255, 0.12)' }} />
          <Typography variant="caption" display="block" mb={0.5} sx={{ opacity: 0.7 }}>
            {t('tasks:catalog.dialog.recurrence_details')}
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2" fontSize="0.875rem">
              <strong>{t('tasks:catalog.dialog.frequency')}:</strong> {getFrequencyLabel(template)}
            </Typography>
          </Stack>
        </>
      )}
    </Box>
  ), [t, getFrequencyLabel]);

  // Stable columns, so the grid doesn't reprocess them on every render
  const columns = useMemo(() => [
    {
      field: 'title',
      headerName: t('tasks:labels.title'),
      flex: 2,
      minWidth: 180,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          {row.task.is_recurring && <Repeat fontSize="small" sx={{ color: 'success.main', flexShrink: 0 }} />}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight="medium" noWrap>{row.task.title}</Typography>
            {row.task.description && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {row.task.description}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: 'due_date',
      headerName: t('tasks:labels.due_date'),
      width: 150,
      renderCell: ({ row }) => (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="body2">
            {row.task.due_date ? new Date(row.task.due_date).toLocaleDateString() : '-'}
          </Typography>
          {row.task.is_overdue && (
            <Chip label={t('tasks:task_card.overdue')} size="small" color="error" sx={{ mt: 0.5, width: 'fit-content' }} />
          )}
        </Box>
      ),
    },
    {
      field: 'completion_date',
      headerName: t('tasks:labels.completion_date'),
      width: 180,
      renderCell: ({ row }) => {
        const stateConfig = getStateConfig(row.task.state);
        return row.task.completion_date ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
            <Typography variant="body2">{new Date(row.task.completion_date).toLocaleDateString()}</Typography>
            <Chip label={stateConfig.label} size="small" color={stateConfig.color} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip label={stateConfig.label} size="small" color={stateConfig.color} variant="outlined" />
          </Box>
        );
      },
    },
    {
      field: 'assigned_to',
      headerName: t('tasks:labels.assigned_to'),
      width: 180,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {row.task.primary_assignee ? (
            <StaffMember staffMember={row.task.primary_assignee} avatarSize={32} imageSize="32x32" nameVariant="body2" />
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'notes',
      headerName: t('equipment:labels.notes'),
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }) => (
        <Typography variant="body2" color="text.secondary" noWrap>{row.notes || '-'}</Typography>
      ),
    },
    {
      field: 'attachments',
      headerName: t('tasks:labels.attachments'),
      width: 150,
      sortable: false,
      renderCell: ({ row }) => renderAttachments(row.task.attachments),
    },
  ], [t]);

  // Don't render the card if user doesn't have task:read permission
  if (!canReadTasks) {
    return null;
  }

  const totalTaskCount = Object.values(counts).reduce((sum, count) => sum + count, 0);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {availableTemplates.length > 0 && (
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              startIcon={<PlaylistAddIcon />}
              onClick={handleOpenMenu}
              size="small"
            >
              {t('equipment:actions.add_from_template')}
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleCloseMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { maxWidth: 400, maxHeight: 400 } }}
            >
              {availableTemplates.map((template) => (
                <Tooltip
                  key={template.template_id}
                  title={renderTemplateDetails(template)}
                  placement="left"
                  arrow
                  enterDelay={300}
                  leaveDelay={0}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'background.paper',
                        color: 'primary.main',
                        maxWidth: 'none',
                        '& .MuiTooltip-arrow': { color: 'background.paper' },
                        boxShadow: 10,
                      },
                    },
                  }}
                >
                  <MenuItem onClick={() => handleUseTemplate(template)} sx={{ py: 1.5 }}>
                    <ListItemIcon>
                      {template.is_recurring ? <LoopIcon /> : <AssignmentIcon />}
                    </ListItemIcon>
                    <ListItemText
                      primary={template.name}
                      secondary={getFrequencyLabel(template)}
                      primaryTypographyProps={{ fontWeight: 'medium' }}
                    />
                  </MenuItem>
                </Tooltip>
              ))}
            </Menu>
          </Box>
        )}

        {countsLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : totalTaskCount === 0 ? (
          <Box 
            flex={1} 
            display="flex" 
            flexDirection="column" 
            justifyContent="center" 
            alignItems="center" 
            py={8}
          >
            <AssignmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('equipment:messages.no_tasks')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('equipment:messages.no_tasks_description')}
            </Typography>
          </Box>
        ) : (
          <Box display="flex" gap={0} flex={1} minHeight={500}>
            {/* Category sidebar */}
            <Box
              sx={{
                width: 200,
                flexShrink: 0,
                borderRight: 1,
                borderColor: 'divider',
              }}
            >
              <List dense disablePadding>
                {RELATION_TYPES.map((type) => {
                  const count = getTaskCount(type);
                  const disabled = count === 0;
                  return (
                    <ListItemButton
                      key={type}
                      selected={activeTab === type}
                      onClick={() => handleTabChange(null, type)}
                      disabled={disabled}
                      sx={{
                        borderRadius: 1,
                        mx: 0.5,
                        '&.Mui-selected': { fontWeight: 600 },
                      }}
                    >
                      <ListItemText
                        primary={t(`equipment:relation_types.${type}`)}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                      />
                      {!disabled && <Chip label={count} size="small" sx={{ ml: 1 }} />}
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>

            {/* Task grid */}
            <Box flex={1} minWidth={0}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.task_equipment_id}
                onRowClick={({ row }) => handleViewTask(row.task.task_id)}
                rowHeight={64}
                disableRowSelectionOnClick
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                loading={pageLoading}
                // The server orders by due date; sorting here would only reorder the current page
                disableColumnSorting
                sx={{
                  border: 0,
                  '& .MuiDataGrid-row': { cursor: 'pointer' },
                  height: '100%',
                }}
              />
            </Box>
          </Box>
        )}
    </Box>
  );
}